import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { tokens } from '@treams/treams-ui';

import { cellColors, pastCellColors } from '../talentReviewColors';
import TalentReviewAxisLabel from './TalentReviewAxisLabel';
import TalentReviewCell from './TalentReviewCell';

const Row = styled(Flex)<{ dragging?: boolean }>`
	width: 100%;
	align-items: flex-start;

	&:not(:last-child) {
		margin-bottom: ${(props) => (props.dragging ? 14 : 24)}px;
	}
`;

const YLabel = styled(Flex)<{ $cell?: boolean }>`
	position: relative;
	flex-grow: 0;
	flex-shrink: 0;
	width: 30px;
	align-items: center;
	${(props) => props.$cell && 'height: 140px;'}
`;

const YLabelText = styled(Box)<{ main?: boolean }>`
	${(props) =>
		props.main
			? `
		color: ${tokens.text.primary};
		font-weight: bold;
	`
			: `
		color: ${tokens.text.secondary};
	`}
`;

const XLabel = styled(Flex)<{ main?: boolean; translateX?: number }>`
	flex: 1;
	min-width: 0;
	margin-left: 10px;
	justify-content: center;

	${(props) => props.translateX && `transform: translateX(${props.translateX}px)`};

	${(props) =>
		props.main
			? `
		color: ${tokens.text.primary};
		font-weight: bold;
	`
			: `
		color: ${tokens.text.secondary};
	`}
`;

export default function TalentReviewGrid(props: {
	template: graphql.TalentReviewTemplate;
	/** True while a person is being dragged; disables cell instruction popovers. */
	dragging?: boolean;
	placements: graphql.TalentReviewGridPlacement[];
	readonly?: boolean;
	/** True when showing an earlier day of the live grid; cells turn neutral grey. */
	past?: boolean;
	focusUserID?: string;
	highlightUserID?: string;
	onSelectUser?: (userID: string) => void;
}) {
	return (
		<Box width="100%">
			<Flex>
				<YLabel mr={2}>
					<YLabelText main>
						<TalentReviewAxisLabel text={props.template.yAxis.label} vertical />
					</YLabelText>
				</YLabel>
				<Box flex={1} minWidth={0}>
					{props.template.yAxis.ticks
						.map((_, row) => (
							<Row key={row}>
								<YLabel $cell>
									<YLabelText>
										<TalentReviewAxisLabel text={props.template.yAxis.ticks[row]} vertical />
									</YLabelText>
								</YLabel>
								{props.template.xAxis.ticks.map((_, column) => (
									<TalentReviewCell
										key={column}
										row={row}
										column={column}
										cell={props.template.cells[row][column]}
										colors={
											props.past
												? pastCellColors
												: cellColors(
														row,
														column,
														props.template.yAxis.ticks.length,
														props.template.xAxis.ticks.length,
													)
										}
										dragging={props.dragging}
										placements={props.placements}
										outdatedAfterDays={props.template.outdatedAfterDays}
										readonly={props.readonly}
										focusUserID={props.focusUserID}
										highlightUserID={props.highlightUserID}
										onSelectUser={props.onSelectUser}
										decorated={
											row === props.template.yAxis.ticks.length - 1 &&
											column === props.template.xAxis.ticks.length - 1
										}
									/>
								))}
							</Row>
						))
						.reverse()}
				</Box>
			</Flex>
			<Box>
				<Row mt={24} mb={0}>
					<YLabel mr={2} />
					<YLabel />
					{props.template.xAxis.ticks.map((_, column) => (
						<XLabel key={column}>
							<TalentReviewAxisLabel text={props.template.xAxis.ticks[column]} />
						</XLabel>
					))}
				</Row>
			</Box>
			<Row mt={2}>
				<YLabel mr={2} />
				<YLabel />
				<XLabel main>
					<TalentReviewAxisLabel text={props.template.xAxis.label} />
				</XLabel>
			</Row>
		</Box>
	);
}
